import { z } from 'zod';
import { amountSchema, createModel, QueryMany, QueryOne, refSchema } from '../../common';
import { invoiceStatusSchema } from './invoice.status.model';
import { dateSchema, nameSchema, objectIdSchema, priceSchema } from '../../validation';
import { invoicePeriodSchema } from './invoice.period.model';
import { invoiceBeneficiarySchema } from './invoice.beneficiary.model';
import { invoiceItemSchema } from './invoice.item.model';
import { invoiceFilterSchema } from './invoice-filters.model';
import { invoiceTagSchema } from './invoice-tags.model';
import { invoiceProofSchema } from './invoice.proof';
import { invoiceCashbackSchema } from './invoice.cashback';
import { invoiceFinancingSchema } from './invoice.financing';
import { UTagSharedSchema } from '../utag';
import { orderPODCopySettingSchema } from '../order/order.settings.model';
import { orderPODCopyStatusSchema } from '../order/order.pod.model';

/**
 * `Invoice db model` — bilateral receivable between two parties in
 * the Hero network.
 *
 * ## Bilateral, not broker-issued
 *
 * Post org-to-org-refactor, an Invoice represents a receivable that
 * one party (`seller`) is owed by another (`buyer`). Hero is no
 * longer a counterparty on the network parties' invoices — those
 * are bilateral and live inside the network. Hero's SaaS billing
 * (`Billing` / `BillingTransaction` / `Subscription`) is a separate
 * stack and is out of scope.
 *
 * Field rename history:
 * - `payer`    → `buyer`   (party paying on this invoice)
 * - `receiver` → `seller`  (party owed on this invoice)
 * - `beneficiaryIds` → `partyIds` ([buyer._id, seller._id])
 */
export const InvoiceSchema = createModel({
  /**
   * - `on_going` = the invoice is still accruing line items.
   * - `not_paid` = closed; the amount has not been paid yet.
   * - `pending` = a wire transfer proof has been uploaded; awaiting finance approval.
   * - `paid` = fully paid.
   * - `cancelled` = void; has no effect on the bilateral Account balance.
   */
  status: invoiceStatusSchema,
  /**
   * Unused amount is mainly used for advance payments, this most likely be zero in credit payments.
   *
   * In advance payments, the invoice will have `paid` status immediately after making the payment.
   *
   * For example, a buyer makes a payment of `1,000 SAR` in advance, then the `unusedAmount` will be `1000`.
   * When the buyer makes a new order for `500 SAR`, then the `unusedAmount` will be updated to be `500`.
   *
   * This will continue till the `unusedAmount` becomes zero which indicates that
   * the buyer has used all of his advance payment in this specific invoice.
   *
   * The currency of the unused amount will be the same as the invoice amount.
   */
  unusedAmount: priceSchema,

  amount: amountSchema,

  /** the title of the invoice */
  title: nameSchema,

  /** this will be used as the main ref for invoices */
  ref: refSchema,

  /** the actual date in which this invoice was `fully` paid */
  paidAt: dateSchema.optional(),

  /** the number of days it took for the invoice to be paid after the `dueDate`.
   *
   * The formula: paidAt - dueDate.
   *
   * - If the result is `0`, then it means the the invoice is paid `on time`.
   * - If the result is a `negative` number, then it means the the invoice is paid `earlier` than the due date.
   * - If the result is a `positive` number, then it means the invoice is paid `later` than the due date.
   *
   * The value will be empty until the invoice has been fully paid.
   */
  daysToPay: z.coerce.number().optional(),
  /**
   * The party that pays on this invoice. The `buyer` field replaces
   * the legacy `payer`. Same {@link invoiceBeneficiarySchema} shape;
   * only the field name on the parent has changed.
   *
   * Carries the full `partySchema` snapshot (with `roles[]` /
   * `ids[]`) so downstream consumers can branch on role without
   * a second resolution.
   */
  buyer: invoiceBeneficiarySchema,

  /**
   * The party that is owed on this invoice. The `seller` field
   * replaces the legacy `receiver`. Same shape, role-neutral name.
   *
   * Sellers see the invoice in their "Money I'm owed" view; the
   * "Finance it now" CTA is gated to the seller (the receivable
   * holder).
   */
  seller: invoiceBeneficiarySchema,

  /** the buyer + seller party ids — `[buyer._id, seller._id]`. */
  partyIds: z.array(objectIdSchema).min(2).max(2),

  /**
   * The `Account` doc that holds this invoice's bilateral balance.
   * Required for new invoices. Resolves the `(buyer, seller, currency)`
   * triple → exactly one Account via canonical ordering inside
   * `AccountService.findOrCreateForPair`.
   *
   * Optional in the schema for read compatibility with pre-refactor
   * invoice docs (no migration), but the capture flow always sets
   * it on new writes.
   */
  accountId: objectIdSchema.optional(),

  /**
   * The Signed Org that issued (recorded the books for) this
   * invoice. Mostly equal to the seller's org for typical
   * receivables, but kept as a separate field so future flows
   * (e.g. a broker issuing on behalf of a carrier) have a place to
   * record the issuer without conflating with the seller party.
   *
   * Optional — pre-refactor docs don't carry it.
   */
  issuedByOrgId: objectIdSchema.optional(),
  /** the date at which the invoice moved from `on_going` to `not_paid`.
   *
   * This will most likely be the date `period.to` of the invoice.
   *
   * this is used more like for the system to know when the invoice was actual closed it could be before or after the `period.to` date.
   *
   * Also sometimes we need to close the invoice before the `period.to` date. So this is where we can do it.
   */
  closedAt: dateSchema.optional(),

  /** invoice proof containing the uploaded invoice image, reference number, and target type */
  invoiceProof: invoiceProofSchema.optional(),

  /** the invoice coverage period such as from Jan 1 to Jan 30. */
  period: invoicePeriodSchema,
  /** the breakdown of items that make up the total `amount` of the invoice.
   *
   * If multi orders for 1 invoice, then it will be aggregated in 1 item and the type will be `order_vehicle`.
   *
   * If empty array, then it means that the invoice is most likely on_going and not yet has orders.
   */
  items: z.array(invoiceItemSchema),
  /**
   * filters are used to filter orders / items that will be added to the invoice.
   *
   * For example, some customer might want to separate orders for Riyadh and Jeddah in separate invoices ... so we can use filters to do that.
   *
   * @TBC --- we need to finish this schema when it's time
   */
  filters: z.array(invoiceFilterSchema).max(0),

  /**
   * the agreement id that is used to create the invoice, and its payment terms.
   *
   * This cloud be empty because some invoices are created without an agreement like for individual customers.
   */
  agreementId: objectIdSchema.optional(),

  /** the date in which the invoice was approved by our Finance team. */
  approvedAt: dateSchema.optional(),

  /** the date & time in which the invoice was rejected by our Finance team. */
  cancelledAt: dateSchema.optional(),

  /**
   * - `advance_payment` = means the invoice is an advance payment such as 10K SAR.
   */
  tags: z.array(invoiceTagSchema),

  UTags: z.array(UTagSharedSchema),

  /** Invoice-level cashback info (set/updated by the daily cashback cron job). */
  cashback: invoiceCashbackSchema.optional(),

  /**
   * Back-link to the `FinancingApplication` that targets this
   * invoice. Presence of this sub-doc is the single source of
   * truth for "this invoice is already in flight or has been
   * financed" — one-shot per invoice. See
   * {@link invoiceFinancingSchema}.
   *
   * Stamped by `InvoiceService.syncInvoiceFinancingFromApplication`
   * on every `FinancingApplication.status` mutation in
   * `FinancingService`. Read by the `financeableOnly` query and by
   * the `useInvoiceInfo` hook's `canFinance` flag.
   */
  financing: invoiceFinancingSchema.optional(),

  /**
   * Mirror of `Order.settings.podCopy` — drives whether the seller
   * is expected to return a paper original of this invoice to the
   * buyer. Seeded at capture time from the captured legs' parent
   * `Order.settings.podCopy`; if any leg requires
   * `'return_original_pod'`, the invoice does too. Defaults to
   * `['pod_image']` (no paper return, common case).
   *
   * Reuses the POD enum verbatim so analytics / translations /
   * downstream consumers handle order POD copies and invoice
   * original copies through the same code path.
   */
  originalCopySettings: z.array(orderPODCopySettingSchema).default(['pod_image']),

  /**
   * Lifecycle of the paper-original-invoice flow. Same 7-value
   * enum as `Order.status.podCopy` (`no_pod_required`,
   * `not_received`, `received`, `under_review`,
   * `ready_to_be_sent`, `sent`, `delivered`).
   *
   * Seeded at capture: `'not_received'` when
   * `originalCopySettings.includes('return_original_pod')`,
   * otherwise `'no_pod_required'`. Read-time fallback: pre-
   * refactor invoice docs that don't carry this field are treated
   * as `'no_pod_required'` by the UI.
   */
  originalCopyStatus: orderPODCopyStatusSchema.default('no_pod_required'),
});

/** `Invoice db model` */
export type Invoice = z.infer<typeof InvoiceSchema.model>;
export type InvoicePartial = z.infer<typeof InvoiceSchema.partial>;
export type InvoiceData = z.infer<typeof InvoiceSchema.data>;
export type InvoiceQueryOne = QueryOne<Invoice>;
export type InvoiceQueryMany = QueryMany<Invoice>;
